import React, { useEffect, useMemo, useState } from "react";

interface DecodeTextProps {
  as?: "h1" | "p";
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  text: string;
}

const characters = "0123456789abcdefghijklmnopqrstuvwxyz";
const numbers = "0123456789";
const scrambleInterval = 34;

const scramble = (
  text: string,
  revealed: number,
  characterSet = characters
) =>
  Array.from(text)
    .map((character, index) => {
      if (/\s|[.,’/]/.test(character) || index < revealed) {
        return character;
      }

      return characterSet[Math.floor(Math.random() * characterSet.length)];
    })
    .join("");

const decodeWord = (word: string, progress: number) => {
  if (progress < 0.3) {
    return scramble(word, 0, numbers);
  }

  const letterProgress = (progress - 0.3) / 0.7;
  const revealedCharacters = Math.floor(word.length * letterProgress);
  return scramble(word, revealedCharacters);
};

const renderWordSequence = (text: string, progress: number) => {
  const words = text.split(/\s+/);
  const wordPosition = progress * words.length;
  const completedWords = Math.floor(wordPosition);
  const currentWordProgress = wordPosition - completedWords;

  return words
    .map((word, index) => {
      if (progress >= 1 || index < completedWords) {
        return word;
      }

      if (index === completedWords) {
        return decodeWord(word, currentWordProgress);
      }

      return scramble(word, 0);
    })
    .join(" ");
};

const DecodeText: React.FC<DecodeTextProps> = ({
  as: Element = "p",
  children,
  delay = 0,
  duration = 1000,
  text,
}) => {
  const lowercaseText = useMemo(() => text.toLowerCase(), [text]);
  const [displayText, setDisplayText] = useState(() =>
    renderWordSequence(lowercaseText, 0)
  );
  const [isDecoded, setIsDecoded] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayText(lowercaseText);
      setIsDecoded(true);
      return;
    }

    let animationFrame = 0;
    let lastScrambleTime = 0;
    let startTime = 0;

    const animate = (time: number) => {
      if (!startTime) startTime = time;

      const elapsed = time - startTime;
      const progress =
        elapsed < delay ? 0 : Math.min((elapsed - delay) / duration, 1);

      if (
        !lastScrambleTime ||
        time - lastScrambleTime >= scrambleInterval ||
        progress >= 1
      ) {
        setDisplayText(renderWordSequence(lowercaseText, progress));
        lastScrambleTime = time;
      }

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setDisplayText(lowercaseText);
        setIsDecoded(true);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [delay, duration, lowercaseText]);

  return (
    <div className={`decode-block ${isDecoded ? "decoded" : ""}`}>
      <Element className="decoded-copy">{children}</Element>
      <Element className="scrambled-copy" aria-hidden="true">
        {displayText}
      </Element>
    </div>
  );
};

export default DecodeText;
